import React, { createContext, useContext, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import HomePage from "@/pages/HomePage";
import CalculatorPage from "@/pages/CalculatorPage";
import VOCalculatorPage from "@/pages/VOCalculatorPage";
import AdminPage from "@/pages/AdminPage";
import HistoryPage from "@/pages/HistoryPage";
import storage from "@/utils/storage";

// Theme Context
const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    try {
      const saved = localStorage.getItem("emcom_dark_mode");
      return saved === "true";
    } catch (e) {
      return false;
    }
  });

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    try {
      localStorage.setItem("emcom_dark_mode", isDark);
    } catch (e) {
      console.warn("localStorage not available for dark mode");
    }
  }, [isDark]);

  const toggleTheme = () => setIsDark(!isDark);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Auth guard component - checks session and midnight expiration
const ProtectedRoute = ({ children }) => {
  const [isValid, setIsValid] = useState(true);
  
  useEffect(() => {
    // Vérification initiale et périodique de l'expiration de session
    const checkSessionExpiry = () => {
      const sessionData = storage.getJSON("emcom_session");
      
      if (!sessionData) {
        setIsValid(false);
        return;
      }
      
      try {
        const loginDate = sessionData.logged_in_at ? new Date(sessionData.logged_in_at).toDateString() : null;
        const today = new Date().toDateString();
        
        // Si la date de connexion n'est pas aujourd'hui, déconnecter l'utilisateur
        if (loginDate && loginDate !== today) {
          storage.removeItem("emcom_session");
          setIsValid(false);
        }
      } catch (e) {
        storage.removeItem("emcom_session");
        setIsValid(false);
      }
    };
    
    // Vérification immédiate
    checkSessionExpiry();
    
    // Vérifier toutes les 30 secondes (pour détecter minuit rapidement)
    const interval = setInterval(checkSessionExpiry, 30000);
    
    return () => clearInterval(interval);
  }, []);
  
  const session = storage.getJSON("emcom_session");
  
  if (!session || !isValid) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function App() {
  return (
    <ThemeProvider>
      <div className="App min-h-screen bg-background text-foreground transition-colors duration-300">
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
            <Route path="/calculator/vo" element={<ProtectedRoute><VOCalculatorPage /></ProtectedRoute>} />
            <Route path="/calculator/:brand" element={<ProtectedRoute><CalculatorPage /></ProtectedRoute>} />
            <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
            <Route path="/admin" element={<AdminPage />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </ThemeProvider>
  );
}

export default App;
